import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

import { AuthGuard } from './components/AuthGuard';
import { AppRouter } from './router';

/**
 * Корневой компонент MiniApp.
 *
 * Stage 5.X.1 (skeleton) — QueryClientProvider + BrowserRouter.
 * Stage 5.X.2 (auth) — <AuthGuard> между провайдерами и роутами:
 *   - На mount автоматически логинит юзера через Telegram WebApp.initData.
 *   - Защищает все нижестоящие страницы от рендера без JWT.
 *   - Показывает retry-экран на login-fail.
 *
 * Stage 5.X.3+ добавит:
 * - <I18nProvider> (i18next + react-i18next).
 * - <ConsentGuard> (152-ФЗ checkpoint поверх AuthGuard).
 * - <ErrorBoundary> + Sentry/GlitchTip.
 *
 * Stage 5.X.4 добавит SSE-подключение через `useEvents` hook.
 *
 * См. docs/tg-miniapp.md §1.6 + §10.2.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // По tg-miniapp.md §6 (Profile) — staleTime 30s для большинства запросов;
      // конкретные query могут переопределять. SSE invalidate'ит активно
      // меняющиеся данные (профиль, подписка) при `subscription.activated`,
      // `payment.confirmed` и т.п. (Stage 5.X.4).
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthGuard>
          <AppRouter />
        </AuthGuard>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
